import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface StepConfirmProps {
  firstName: string;
  lastName: string;
  email: string;
  selectedStyles: string[];
  selectedCompanions: string[];
  selectedSeasons: string[];
  onSubmit: () => Promise<void>;
  onBack: () => void;
  error?: string | null;
}

const STYLE_LABELS: Record<string, string> = {
  beach: 'Beach House',
  mountain: 'Mountain Chalet',
  city: 'City Apartment',
  village: 'Village House',
};

const COMPANION_LABELS: Record<string, string> = {
  solo: 'Solo',
  couple: 'Couple',
  family: 'Family',
  friends: 'Friends',
};

const SEASON_LABELS: Record<string, string> = {
  summer: 'Summer',
  winter: 'Winter',
  'spring-fall': 'Spring/Fall',
  anytime: 'Anytime',
};

export const StepConfirm: React.FC<StepConfirmProps> = ({
  firstName,
  lastName,
  email,
  selectedStyles,
  selectedCompanions,
  selectedSeasons,
  onSubmit,
  onBack,
}) => {
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptTerms) return;
    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasPreferences = selectedStyles.length > 0 || selectedCompanions.length > 0 || selectedSeasons.length > 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Ready to join?
        </h1>
        <p className="text-gray-500 mb-8">
          Review your details and create your account
        </p>

        {/* Summary card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Name</p>
              <p className="text-gray-900 font-medium">{firstName} {lastName}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">Email</p>
              <p className="text-gray-900 font-medium">{email}</p>
            </div>

            {hasPreferences && (
              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-2">
                  Travel Preferences
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedStyles.map((s) => (
                    <span key={s} className="px-3 py-1 bg-primary-50 text-primary-700 text-xs rounded-full font-medium">
                      {STYLE_LABELS[s] || s}
                    </span>
                  ))}
                  {selectedCompanions.map((c) => (
                    <span key={c} className="px-3 py-1 bg-blue-50 text-blue-700 text-xs rounded-full font-medium">
                      {COMPANION_LABELS[c] || c}
                    </span>
                  ))}
                  {selectedSeasons.map((s) => (
                    <span key={s} className="px-3 py-1 bg-amber-50 text-amber-700 text-xs rounded-full font-medium">
                      {SEASON_LABELS[s] || s}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Terms checkbox */}
          <label className="flex items-start gap-3 cursor-pointer mb-6">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-600">
              I agree to the{' '}
              <Link to="/terms" className="text-primary-600 hover:underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-primary-600 hover:underline">
                Privacy Policy
              </Link>
            </span>
          </label>

          <button
            type="submit"
            disabled={!acceptTerms || isSubmitting}
            className="w-full py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creating your account...
              </span>
            ) : (
              'Create My Account'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StepConfirm;
